import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import UiButton from '@/ui/UiButton/UiButton';
import useAuth from '@/auth/use-auth';
import UiSwitcher from '@/ui/UiSwitcher/UiSwitcher';
import { getUserScore } from '@/auth/api';
import { getClientConfig } from '@/hooks/helpers';

export default function Header() {
  const navigate = useNavigate();
  const { access_token, refresh_token, token, ksid, refreshToken, setScore, setUserInfo, id_token, revoke, getTokenManually, sendAuthSuccessEvent, sendTokenReceivedEvent, openPersonalAccountCards, getUserInfo, getForcedAuthVariants, refreshSdkConfigFromServer } = useAuth();
  const [isDarkTheme, setIsDarkTheme] = React.useState<boolean>(false);
  const [loginTagValues, setLoginTagValues] = React.useState<string | null>(null);
  const isLiteAuthenticated = Boolean(token);
  const isPublicAuthenticated = Boolean(access_token);
  const isAuthenticated = isLiteAuthenticated || isPublicAuthenticated;

  const onThemeChange = (value: boolean) => {
    setIsDarkTheme(value);
    if (value) {
      document.body.classList.add('theme-dark');
    } else {
      document.body.classList.remove('theme-dark');
    }
  }

  const getInfo = async (): Promise<void> => {
    try {
      const userInfo = await getUserInfo(access_token);
      if (!userInfo) return;
      setUserInfo(JSON.stringify(userInfo));
    } catch (e: unknown) {
      console.error('Ошибка в методе getInfo: ', e);
    }
  }

  const getScore = async (): Promise<void> => {
    const userInfo = await getUserInfo(access_token);

    if (!userInfo) return;

    const clientConfig = getClientConfig();

    function base64urlDecode(str: string) {
      // Заменяем символы URL-безопасного Base64 на стандартные символы Base64
      str = str.replace(/-/g, '+').replace(/_/g, '/');
      // Добавляем недостающие символы '=' для корректного декодирования
      while (str.length % 4) {
        str += '=';
      }
      return atob(str);
    }

    function extractKsid(tokenId: string) {
      const chunks = tokenId.split('.');
      const payload = chunks[1];
      const jsonString = base64urlDecode(payload);
      const json = JSON.parse(jsonString);
      return json['ksid'];
    }

    const tokenId = id_token;
    const extractedKsid = tokenId ? extractKsid(tokenId) : '';
    const scoreKsid = isLiteAuthenticated ? ksid : extractedKsid;

    const client_id = clientConfig.clientId;

    try {
      const scoreResult = await getUserScore(clientConfig.backendUrl, scoreKsid, client_id, userInfo.sub);
      if (!scoreResult) return;
      setScore(JSON.stringify(scoreResult));
    } catch (e: unknown) {
      console.error('Ошибка в методе getScore: ', e);
    }
  }

  const showLoginTags = (): void => {
    const values = getForcedAuthVariants();
    setLoginTagValues(values.join(', '));
  };

  const handleGazpromPayClick = (): void => {
    openPersonalAccountCards(() => {
      getInfo();
    }).catch(console.error);
  };

  return (
    <div className="header">
      <nav className="menu">
        <div className='page-text theme-buttons' data-testid="theme-switch">
          Theme {isDarkTheme ? 'Dark' : 'Light'}
          <UiSwitcher value={isDarkTheme} onChange={(e) => onThemeChange(e)} />
        </div>
        <UiButton
          type='secondary'
          dataTestId="fetch-sdk-config-btn"
          onClick={() => refreshSdkConfigFromServer().catch(console.error)}
        >
          Запросить конфиг
        </UiButton>
        {
          isAuthenticated ?
            <>
              <UiButton
                type='secondary'
                dataTestId="get-score-btn"
                onClick={getScore}
              >
                Получить score
              </UiButton>
              <UiButton
                type='secondary'
                dataTestId="get-info-btn"
                onClick={getInfo}
              >
                Получить userInfo
              </UiButton>
              <UiButton
                type='secondary'
                dataTestId="gazprom-pay-btn"
                onClick={handleGazpromPayClick}
              >
                Gazprom Pay
              </UiButton>
              <UiButton
                type='secondary'
                dataTestId="refresh-token-btn"
                onClick={() => refreshToken(refresh_token || '')}
              >
                Обновить токен
              </UiButton>
              {isPublicAuthenticated && (
                <>
                  <UiButton
                    type='secondary'
                    dataTestId="revoke-btn"
                    onClick={() => revoke(access_token || '').then(() => navigate('/'))}
                  >
                    Revoke
                  </UiButton>
                  {getClientConfig().preventAutoStorageCleanup && (
                    <UiButton
                      type='secondary'
                      dataTestId="send-auth-success-event-btn"
                      onClick={sendAuthSuccessEvent}
                    >
                      Отправить событие успешной авторизации
                    </UiButton>
                  )}
                  <UiButton
                    type='secondary'
                    dataTestId="send-get-token-event-btn"
                    onClick={sendTokenReceivedEvent}
                  >
                    Отправить событие получения токена
                  </UiButton>
                </>
              )}
              <UiButton
                type='secondary'
                dataTestId="exit-btn"
                onClick={() => navigate('/logout')}
              >
                Выйти
              </UiButton>
            </> :
            <>
              <UiButton
                type='secondary'
                dataTestId="get-token-manually"
                onClick={getTokenManually}
              >
                Получить токен вручную
              </UiButton>
              {
                loginTagValues === null ? (
                  <UiButton
                    type='secondary'
                    dataTestId="show-login-tag-values"
                    onClick={showLoginTags}
                  >
                    Показать значения login_tag
                  </UiButton>
                ) : (
                  <div className="page-token" data-testid="login-tag-values">
                    {loginTagValues}
                  </div>
                )
              }
            </>
        }
      </nav>
    </div>
  );
}
